import GameResource from "./GameResource";
import { useState, useRef, useEffect } from "react";

function ColorPicker({ children, onSelect }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [open]);

    return (
        <div ref={ref} className="relative">
            <div onClick={() => setOpen((prev) => !prev)}>
                {children}
            </div>
            { open && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 z-30 flex gap-2 bg-base-800/95 backdrop-blur-sm border border-white/10 rounded-full px-2.5 md:px-3 py-2 md:py-1.5 shadow-card">
                    {/* 44px touch targets on phones; the smaller dots are fine with a mouse */}
                    <button
                        className="w-11 h-11 md:w-8 md:h-8 rounded-full bg-status-green hover:scale-110 active:scale-95 transition-transform shadow-md"
                        onClick={() => { onSelect('G'); setOpen(false); }}
                        title="Green" />
                    <button
                        className="w-11 h-11 md:w-8 md:h-8 rounded-full bg-status-yellow hover:scale-110 active:scale-95 transition-transform shadow-md"
                        onClick={() => { onSelect('Y'); setOpen(false); }}
                        title="Yellow" />
                    <button
                        className="w-11 h-11 md:w-8 md:h-8 rounded-full bg-status-red hover:scale-110 active:scale-95 transition-transform shadow-md"
                        onClick={() => { onSelect('R'); setOpen(false); }}
                        title="Red" />
                </div>
            )}
        </div>
    );
}

function GameResourceList({ resources, picks, results, setResults, choice, setChoice, interactive, possibilityCounts, allResources }) {
    const handleResourceResultClick = (index, color) => {
        setChoice(choice);
        setResults((prev) => {
            const newResults = { ...prev };
            newResults[choice][index] = color;
            return newResults;
        });
    };

    const isLastRound =
        (choice === 3 && resources.length === 3) ||
        (choice === 4 && resources.length === 4) ||
        (choice === 5 && resources.length >= 5);

    const showLabel = !!possibilityCounts;

    const gameResources = [0, 1, 2, 3, 4].map((index) => {
        const possSet = possibilityCounts ? possibilityCounts[index] : null;
        const possCount = possSet ? possSet.size : null;
        const possibleResourceNames = possSet && allResources
            ? [...possSet].map(idx => allResources[idx])
            : null;

        if (!resources[picks[index]]) {
            return <GameResource key={index} disabled showLabel={showLabel} possibilityCount={possCount} />;
        }

        if (isLastRound || !interactive) {
            return <GameResource key={index} disabled value={resources[picks[index]]} result={results[index]} possibilityCount={possCount} possibleResources={possibleResourceNames} showLabel={showLabel} />;
        }

        return (
            <ColorPicker key={index} onSelect={(color) => handleResourceResultClick(index, color)}>
                <GameResource
                    result={results[index]}
                    value={resources[picks[index]]}
                    possibilityCount={possCount}
                    possibleResources={possibleResourceNames}
                    showLabel={showLabel} />
            </ColorPicker>
        );
    });

    return (
        <div className="flex gap-1 md:gap-2">
            {gameResources}
        </div>
    )
}

export default GameResourceList;
